import { useEffect, useState } from 'react';
import { Mail, Plus, Save, Trash2, Webhook, X } from 'lucide-react';
import { connectionProfileService } from '../services/connectionProfileService';

type SmtpConfig = {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  pass: string;
  from: string;
};

type WebhookConfig = {
  baseUrl: string;
  authType: 'none' | 'basic' | 'token';
  authUser: string;
  authPass: string;
  authToken: string;
  defaultHeaders: string;
};

type Profile = {
  id?: string;
  name: string;
  type: 'smtp' | 'webhook';
  config: SmtpConfig | WebhookConfig;
};

const defaultSmtpConfig = (): SmtpConfig => ({ host: '', port: 587, secure: false, user: '', pass: '', from: '' });
const defaultWebhookConfig = (): WebhookConfig => ({ baseUrl: '', authType: 'none', authUser: '', authPass: '', authToken: '', defaultHeaders: '{\n  "Content-Type": "application/json"\n}' });

export function ConnectionProfiles({ onSelect }: { onSelect?: (profile: any) => void }) {
  const [profiles, setProfiles] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Profile>({ name: '', type: 'smtp', config: defaultSmtpConfig() });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await connectionProfileService.getAll();
      setProfiles(Array.isArray(res?.data) ? res.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const openNew = (type: 'smtp' | 'webhook') => {
    setEditingId(null);
    setForm({ name: `Yeni ${type === 'smtp' ? 'SMTP' : 'Webhook'} Profili`, type, config: type === 'smtp' ? defaultSmtpConfig() : defaultWebhookConfig() });
    setShowForm(true);
  };

  const openEdit = (profile: any) => {
    setEditingId(String(profile.id));
    setForm({ id: String(profile.id), name: profile.name, type: profile.type, config: profile.config || {} });
    setShowForm(true);
  };

  const save = async () => {
    const payload = { name: form.name, type: form.type, config: form.config };
    if (editingId) await connectionProfileService.update(editingId, payload);
    else await connectionProfileService.create(payload);
    setShowForm(false);
    setEditingId(null);
    void load();
  };

  const del = async (id: string) => {
    if (!confirm('Bu profili silmek istediğinize emin misiniz?')) return;
    await connectionProfileService.delete(id);
    void load();
  };

  const applyEdit = <K extends keyof (SmtpConfig & WebhookConfig)>(key: K, value: any) => {
    setForm((current) => ({ ...current, config: { ...current.config, [key]: value } }));
  };

  if (loading) return <div className="text-sm text-slate-500">Yükleniyor...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Bağlantı Profilleri</h2>
          <p className="text-sm text-slate-500">SMTP ve Webhook bağlantı profillerini yönetin. Otomasyon aksiyonlarında bu profilleri kullanabilirsiniz.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => openNew('smtp')} className="inline-flex items-center gap-2 rounded-2xl border border-pink-200 bg-pink-50 px-4 py-2 text-sm font-medium text-pink-700 hover:bg-pink-100"><Mail className="h-4 w-4" />SMTP Profili</button>
          <button type="button" onClick={() => openNew('webhook')} className="inline-flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"><Webhook className="h-4 w-4" />Webhook Profili</button>
        </div>
      </div>

      {profiles.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">Henüz bağlantı profili eklenmedi.</div>}

      <div className="grid gap-3 md:grid-cols-2">
        {profiles.map((profile) => (
          <div key={profile.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`rounded-xl p-2 ${profile.type === 'smtp' ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'}`}>
                  {profile.type === 'smtp' ? <Mail className="h-5 w-5" /> : <Webhook className="h-5 w-5" />}
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{profile.name}</div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${profile.type === 'smtp' ? 'bg-pink-50 text-pink-600' : 'bg-blue-50 text-blue-600'}`}>{profile.type === 'smtp' ? 'SMTP' : 'Webhook'}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => openEdit(profile)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">Düzenle</button>
                <button type="button" onClick={() => del(String(profile.id))} className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50"><Trash2 className="inline h-3 w-3" /></button>
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              {profile.type === 'smtp' ? (
                <span>{(profile.config as SmtpConfig)?.host || 'Host ayarlanmamış'}</span>
              ) : (
                <span>{(profile.config as WebhookConfig)?.baseUrl || 'URL ayarlanmamış'}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">{editingId ? 'Profili Düzenle' : 'Yeni Profil'}</h3>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="rounded-xl border border-slate-200 p-2 hover:bg-slate-50"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Profil Adı</label>
                <input value={form.name} onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Tür</label>
                <select value={form.type} onChange={(e) => setForm((current) => ({ ...current, type: e.target.value as any, config: e.target.value === 'smtp' ? defaultSmtpConfig() : defaultWebhookConfig() }))} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm">
                  <option value="smtp">SMTP</option>
                  <option value="webhook">Webhook</option>
                </select>
              </div>
              {form.type === 'smtp' ? (
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="md:col-span-2"><label className="mb-1 block text-sm font-medium text-slate-700">Host</label><input value={(form.config as SmtpConfig).host} onChange={(e) => applyEdit('host', e.target.value)} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" /></div>
                  <div><label className="mb-1 block text-sm font-medium text-slate-700">Port</label><input type="number" value={(form.config as SmtpConfig).port} onChange={(e) => applyEdit('port', Number(e.target.value))} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" /></div>
                  <div className="flex items-end pb-3"><label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={(form.config as SmtpConfig).secure} onChange={(e) => applyEdit('secure', e.target.checked)} /> SSL/TLS</label></div>
                  <div><label className="mb-1 block text-sm font-medium text-slate-700">Kullanıcı</label><input value={(form.config as SmtpConfig).user} onChange={(e) => applyEdit('user', e.target.value)} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" /></div>
                  <div><label className="mb-1 block text-sm font-medium text-slate-700">Şifre</label><input type="password" value={(form.config as SmtpConfig).pass} onChange={(e) => applyEdit('pass', e.target.value)} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" /></div>
                  <div className="md:col-span-2"><label className="mb-1 block text-sm font-medium text-slate-700">Gönderen Adresi</label><input value={(form.config as SmtpConfig).from} onChange={(e) => applyEdit('from', e.target.value)} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" placeholder="operasyon@firma.com" /></div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div><label className="mb-1 block text-sm font-medium text-slate-700">Temel URL</label><input value={(form.config as WebhookConfig).baseUrl} onChange={(e) => applyEdit('baseUrl', e.target.value)} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" placeholder="https://api.example.com" /></div>
                  <div><label className="mb-1 block text-sm font-medium text-slate-700">Kimlik Doğrulama</label><select value={(form.config as WebhookConfig).authType} onChange={(e) => applyEdit('authType', e.target.value)} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm"><option value="none">Yok</option><option value="basic">Basic Auth</option><option value="token">Bearer Token</option></select></div>
                  {(form.config as WebhookConfig).authType === 'basic' && (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div><label className="mb-1 block text-sm font-medium text-slate-700">Kullanıcı</label><input value={(form.config as WebhookConfig).authUser} onChange={(e) => applyEdit('authUser', e.target.value)} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" /></div>
                      <div><label className="mb-1 block text-sm font-medium text-slate-700">Şifre</label><input type="password" value={(form.config as WebhookConfig).authPass} onChange={(e) => applyEdit('authPass', e.target.value)} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" /></div>
                    </div>
                  )}
                  {(form.config as WebhookConfig).authType === 'token' && (
                    <div><label className="mb-1 block text-sm font-medium text-slate-700">Token</label><input value={(form.config as WebhookConfig).authToken} onChange={(e) => applyEdit('authToken', e.target.value)} className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" /></div>
                  )}
                  <div><label className="mb-1 block text-sm font-medium text-slate-700">Varsayılan Header'lar (JSON)</label><textarea value={(form.config as WebhookConfig).defaultHeaders} onChange={(e) => applyEdit('defaultHeaders', e.target.value)} rows={5} className="w-full rounded-xl border border-slate-300 px-3 py-3 font-mono text-xs" /></div>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="rounded-2xl border border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">İptal</button>
              <button type="button" onClick={save} disabled={!form.name} className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"><Save className="h-4 w-4" />Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
